import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Users,
  Award,
  Clock,
  Heart,
  TrendingUp,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

export default function PhysiotherapyPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Physiotherapy");


  const subspecialties = [
    {
      title: "Orthopaedic Physiotherapy",
      description: "Back pain, neck pain, joint stiffness, arthritis & post-fracture rehab.",
      image: "/OrthopaedicPhysiotherapy.jpg",
    },
    {
      title: "Neurological Physiotherapy",
      description: "Stroke rehab, Parkinson's, spinal cord injury & nerve disorders.",
      image: "/NeurologicalPhysiotherapy.jpg",
    },
    {
      title: "Sports Physiotherapy",
      description: "Ligament injuries, muscle tears, sprains & athlete conditioning.",
      image: "/SportsPhysiotherapy.jpg",
    },
    {
      title: "Post-Surgical Rehabilitation",
      description: "Knee replacement, spine surgery & fracture recovery.",
      image: "/Post-SurgicalRehabilitation.jpg",
    },
    {
      title: "Pediatric Physiotherapy",
      description: "Developmental delays, gait correction & muscle strengthening for children.",
      image: "/PediatricPhysiotherapy.jpg",
    },
    {
      title: "Geriatric Physiotherapy",
      description: "Balance training, fall prevention & age-related mobility issues.",
      image: "/GeriatricPhysiotherapy.jpg",
    },
  ];

  const treatments = [
    "Manual Therapy & Mobilizations",
    "Exercise Therapy & Strength Training",
    "Dry Needling & Trigger Point Therapy",
    "Electrotherapy (IFT, TENS, Ultrasound)",
    "Kinesio Taping & Orthopaedic Support",
    "Post-Operative Rehab (TKR, ACL, Spine Surgery)",
    "Stroke & Neurological Rehabilitation",
    "Sports Injury Rehab & Fitness Conditioning",
    "Posture Correction & Ergonomic Training",
  ];

  const whyChoose = [
    {
      icon: Users,
      title: "Experienced Physiotherapists",
      description: "Experienced and certified physiotherapists with specialized expertise",
    },
    {
      icon: Award,
      title: "Personalized Treatment Plans",
      description: "Personalized treatment plans based on comprehensive assessment",
    },
    {
      icon: TrendingUp,
      title: "Advanced Equipment",
      description: "Advanced equipment for faster and safer recovery outcomes",
    },
    {
      icon: Heart,
      title: "Integrated Care",
      description: "Integrated care with Orthopaedics, Sports Medicine & Neurosurgery",
    },
    {
      icon: Clock,
      title: "Comfortable Therapy Rooms",
      description: "Comfortable therapy rooms with modern rehabilitation tools",
    },
  ];

  const articles = [
    {
      title: "Top Exercises for Reducing Back and Neck Pain",
      excerpt:
        "Discover effective exercises and stretches that can help alleviate chronic back and neck pain naturally.",
    },
    {
      title: "How Physiotherapy Helps After Knee Replacement Surgery",
      excerpt:
        "Learn about the critical role of physiotherapy in recovery after knee replacement surgery.",
    },
    {
      title: "Sports Injuries: Importance of Early Rehabilitation",
      excerpt:
        "Understanding why early intervention and proper rehabilitation are crucial for sports injury recovery.",
    },
    {
      title: "Stroke Rehabilitation — What to Expect",
      excerpt:
        "A comprehensive guide to stroke rehabilitation and the journey to regaining independence.",
    },
  ];

  const faqs = [
    {
      question: "Do you provide physiotherapy after surgery?",
      answer:
        "Yes, we offer complete post-operative rehabilitation for orthopedic, neuro, and general surgeries. Our physiotherapists work closely with surgeons to create personalized recovery programs that help you regain strength, mobility, and function safely and effectively.",
    },
    {
      question: "How many physiotherapy sessions do I need?",
      answer:
        "It depends on your condition; the physiotherapist will design a personalized plan after evaluation. Typically, acute conditions may require 6-12 sessions, while chronic conditions or post-surgical rehabilitation may need ongoing therapy over several weeks to months.",
    },
    {
      question: "Do you treat sports injuries?",
      answer:
        "Yes, we provide specialized sports rehab, strengthening, and return-to-sport programs. Our sports physiotherapy services include injury prevention, performance enhancement, and comprehensive rehabilitation to help athletes return to their sport safely and confidently.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Physiotherapy Department",
    "url": "https://mamshospitals.com/physiotherapy",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/Physiotherapy.jpg",
    "description": "MAMS Hospitals provides advanced physiotherapy and rehabilitation services in Hyderabad including sports physiotherapy, neurological rehabilitation, orthopedic physiotherapy, post-surgical rehab, pain management, and mobility restoration.",
    "medicalSpecialty": "Physiotherapy",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalTherapy",
        "name": "Orthopaedic Physiotherapy"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Sports Injury Rehabilitation"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Neurological Rehabilitation"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Post-Surgical Rehabilitation"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Pain Management Therapy"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do you provide physiotherapy after surgery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides post-operative physiotherapy and rehabilitation programs for orthopedic, neurological, and general surgeries."
        }
      },
      {
        "@type": "Question",
        "name": "Do you treat sports injuries?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals offers sports physiotherapy, rehabilitation, strengthening exercises, and athlete recovery programs."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide neurological rehabilitation?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, neurological physiotherapy is available for stroke recovery, spinal cord injuries, Parkinson’s disease, and mobility disorders."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Physiotherapy Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/physiotherapy",
    "description": "Best physiotherapy hospital in Hyderabad offering orthopedic physiotherapy, sports rehab, neurological rehabilitation, post-surgical rehab, pain management, and mobility restoration.",
    "keywords": [
      "Best Physiotherapy Center Hyderabad",
      "Physiotherapy Hospital Hyderabad",
      "Sports Physiotherapy Hyderabad",
      "Neurological Rehabilitation Hyderabad",
      "Post Surgical Rehab Hyderabad",
      "Pain Management Physiotherapy Hyderabad",
      "Physiotherapy Center Bachupally"
    ]
  }
];

  return (
    <>
  <SEO
    title="Best Physiotherapy & Rehabilitation Centre in Hyderabad | MAMS Hospitals"
    description="MAMS Hospitals offers advanced physiotherapy and rehabilitation services in Hyderabad including sports physiotherapy, neurological rehabilitation, orthopedic physiotherapy, pain management, and post-surgical rehab."
    keywords="best physiotherapy center Hyderabad,
physiotherapy hospital Hyderabad,
sports physiotherapy Hyderabad,
neurological rehabilitation Hyderabad,
orthopedic physiotherapy Hyderabad,
post surgical rehab Hyderabad,
pain management physiotherapy Hyderabad,
stroke rehabilitation Hyderabad,
sports injury rehabilitation Hyderabad,
dry needling Hyderabad,
physiotherapy clinic Bachupally,
MAMS Hospitals Physiotherapy"
    schema={schema}
  />
    <div className="flex flex-col min-h-screen">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Physiotherapy & Rehabilitation Centre in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Regain Movement, Relieve Pain & Return to Active Life
              </h1>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                MAMS Hospitals is recognized as one of the Best Physiotherapy & Rehabilitation Centres in Hyderabad,
                offering advanced physical therapy, pain management, and customized rehabilitation programs for patients
                of all ages.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl z-10">
                <div className="text-3xl font-bold">Expert Care</div>
                <div className="text-sm">Evidence-Based</div>
              </div>

              <img
                src="/Physiotherapy.jpg"
                alt="Physiotherapy at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/physiotherapy"
      />
            </div>

          </div>

        </div>
      </section>

      {/* Sub-Specialties */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sub-Specialities in Physiotherapy
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive rehabilitation expertise across multiple specializations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-teal-500"
              >
                <CardContent className="p-0">

                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={specialty.image}
                      alt={specialty.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{specialty.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{specialty.description}</p>
                  </div>

                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* Treatments */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Treatments & Procedures
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Evidence-based techniques and modern equipment for optimal recovery
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((treatment, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm"
              >
                <CheckCircle2 className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700 font-medium">{treatment}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose MAMS Hospitals for Physiotherapy
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted rehabilitation excellence with patient-centered care
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">

                    <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-teal-600" />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>

                  </CardContent>
                </Card>
              );
            })}
          </div>

        </div>
      </section>

      {/* Specialist */}
                {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-0 max-w-4xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Get answers to common questions about physiotherapy
            </p>
          </div>

          <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>

                <AccordionTrigger className="text-left px-6 hover:text-teal-600">
                  {faq.question}
                </AccordionTrigger>

                <AccordionContent className="px-6 text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>

              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department</p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
